import type {
  BooleanEntityCustomFormField,
  CoreNode,
  DateTimeEntityCustomFormField,
  DateTimeField,
  DateTimeInputField,
  EntityCustomFormField,
  FloatEntityCustomFormField,
  FloatInputField,
  FloatListSelectField,
  FloatPossibleValue,
  FloatRadioField,
  FloatRangeField,
  FloatSelectField,
  Form as FormSchema,
  InheritableBooleanSetting,
  InheritableDateTimeSetting,
  InheritableFloatListSettingWithPossibleValues,
  InheritableFloatSetting,
  InheritableFloatSettingWithPossibleValues,
  InheritableIntListSettingWithPossibleValues,
  InheritableIntSetting,
  InheritableIntSettingWithPossibleValues,
  InheritableLongSetting,
  InheritableStringListSettingWithPossibleValues,
  InheritableStringSetting,
  InheritableStringSettingWithPossibleValues,
  IntEntityCustomFormField,
  IntInputField,
  IntListSelectField,
  IntPossibleValue,
  IntRadioField,
  IntRangeField,
  IntSelectField,
  ListFloatEntityCustomFormField,
  ListIntEntityCustomFormField,
  ListStringEntityCustomFormField,
  LongEntityCustomFormField,
  LongInputField,
  LongRangeField,
  PasswordField as PasswordFieldInput,
  RelativeDate,
  Scalars,
  SettingInvalidReason,
  StringEntityCustomFormField,
  StringInputField,
  StringListSelectField,
  StringPossibleValue,
  StringRadioField,
  StringSelectField,
  TextareaField as TextareaFieldInput
} from '@aurora/shared-generated/types/graphql-schema-types';
import {
  CustomFieldValueType,
  DateTimeCustomFormFieldType,
  FloatCustomFormFieldType,
  FloatListCustomFormFieldType,
  IntCustomFormFieldType,
  IntListCustomFormFieldType,
  LongCustomFormFieldType,
  StringCustomFormFieldType,
  StringListCustomFormFieldType
} from '@aurora/shared-generated/types/graphql-schema-types';
import type { FormatMessage } from '@aurora/shared-types/texts';
import { capitalizeFirst } from '@aurora/shared-utils/helpers/objects/StringHelper';
import dynamic from 'next/dynamic';
import type { MutableRefObject } from 'react';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { FieldValues, PathValue } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form/dist/types';
import type { FieldPath, FieldPathValue } from 'react-hook-form/dist/types/path';
import { SharedComponent } from '../../../enums';
import { editorSettingsForCustomField } from '../../../helpers/custom/CustomFieldEditorHelper';
import {
  type DateTimeValue,
  getDefaultValueForCustomField,
  getFormFieldInputType,
  getRelativeDate
} from '../../../helpers/custom/CustomFieldHelper';
import FormBuilder from '../../../helpers/form/FormBuilder/FormBuilder';
import { isFieldVisible } from '../../../helpers/form/FormHelper/FormHelper';
import Loading from '../../common/Loading/Loading';
import TextChip from '../../common/TextChip/TextChip';
import useTranslation from '../../useTranslation';
import type { CheckFieldSpec } from '../CheckField/CheckField';
import CheckField from '../CheckField/CheckField';
import type { DatetimeSpec } from '../DateTimeEditorField/DateTimeEditorField';
import DateTimeEditorField from '../DateTimeEditorField/DateTimeEditorField';
import {
  CheckFieldVariant,
  FormCheckInputType,
  FormFieldVariant,
  FormInputFieldInputType
} from '../enums';
import type { InputFieldSpec } from '../InputField/InputField';
import InputField from '../InputField/InputField';
import PasswordField from '../PasswordField/PasswordField';
import type { PasswordFieldSpec } from '../PasswordField/types';
import type { PlaceholderFormFieldSpec } from '../PlaceholderFormField/PlaceholderFormField';
import PlaceholderFormField from '../PlaceholderFormField/PlaceholderFormField';
import type { RadioFieldSpec } from '../RadioField/RadioField';
import RadioField from '../RadioField/RadioField';
import type { RangeFieldSpec } from '../RangeField/RangeField';
import RangeField from '../RangeField/RangeField';
import type { RichTextEditorSpec } from '../RichTextEditorField/RichTextEditorField';
import type { SearchableMultiSelectFieldSpec } from '../SearchableMultiSelectField/SearchableMultiSelectField';
import SearchableMultiSelectField from '../SearchableMultiSelectField/SearchableMultiSelectField';
import type { SelectFieldSpec } from '../SelectField/SelectField';
import SelectField from '../SelectField/SelectField';
import type { TextAreaSpec } from '../TextAreaField/TextAreaField';
import TextAreaField from '../TextAreaField/TextAreaField';
import type {
  DebounceProps,
  FormButton,
  FormFieldProps,
  FormFieldSpecDefinition,
  FormFieldType,
  FormSpec,
  IsVisibleFieldWatcher,
  PossibleValue,
  PossibleValueType
} from '../types';
import { useFormWatch } from '../useFormWatch';
import localStyles from './CustomField.module.pcss';

export interface CustomFieldSettingLabelProps {
  /**
   * Text to be displayed as Label.
   */
  labelText: string;
  /**
   * When provided, displays the "parent override" badge indicating that parent node whose setting is being overridden
   */
  overridingParentNode?: {
    /**
     * The title of the overriding parent node
     */
    title: string;
    /**
     * The node type of the overriding parent node
     */
    nodeType: string;
  };
}

export interface CustomFieldModalSettingValueProps {
  /**
   * The title of the setting.
   */
  settingTitle: string;
  /**
   * The value of the setting.
   */
  value: string[] | string | number;
  /**
   * Additional description of the setting
   */
  settingDescription?: string;
  /**
   * When provided, displays the "parent override" badge indicating that parent node whose setting is being overriden
   */
  overridingParentNode?: {
    /**
     * The title of the overriding parent node
     */
    title: string;
    /**
     * The node type of the overriding parent node
     */
    nodeType: string;
  };
  /**
   * whether to use value or not
   */
  useValue?: boolean;
}

export interface CustomFieldModalSettingProps<ModalFormDataT extends FieldValues> {
  /**
   * The form spec for the form that will display in the modal.
   */
  modalFormSpec: FormSpec<ModalFormDataT>;
  /**
   * Specifies the settings info to display in preview.
   */
  settingValueProps: CustomFieldModalSettingValueProps[];

  /**
   * Default format message function for custom fields.
   */
  formatDefaultMessage: FormatMessage;

  /**
   * Name of the custom field.
   */
  customName: string;

  /**
   * Field label to use for the modal field.
   */
  modalFieldLabelKey?: string;
  /**
   * Classname to apply to SettingsModalForm's title info element.
   */
  titleInfoClassName?: string;
  /**
   * Whether to use static backdrop on form modal.
   */
  useStaticBackdrop?: boolean;
  /**
   * Size of the Modal to load
   */
  modalSize?: 'sm' | 'lg';
}

export interface CustomFieldSpec<NameT extends FieldPath<DataT>, DataT extends FieldValues>
  extends FormFieldType<NameT, DataT, FormFieldVariant.CUSTOM_FIELD> {
  /**
   * The collections of custom field definition to display in the form
   */
  fields: Array<EntityCustomFormField>;
  /**
   * The id which defines the entity.
   */
  entityId: Scalars['ID']['output'];
  /**
   * Function to generate a SettingFormLabel if this is for a setting.
   * @param labelProps the props used to generate the label.
   */
  settingLabelFunction?: (labelProps: CustomFieldSettingLabelProps) => React.ReactNode;

  /**
   * Function to generate a SettingModalField if this is for a setting.
   * @param modalProps the props used to generate the modal.
   */
  settingModalFunction?: (
    /**
     * The props used to generate the modal.
     */
    modalProps: CustomFieldModalSettingProps<DataT>
  ) => React.ComponentType<React.PropsWithChildren<{ formMethods: UseFormReturn<DataT> }>>;

  /**
   * Reference of the element which should contain the overlay(s) in the editor such as emoji, mentions.
   */
  formBodyReference?: MutableRefObject<HTMLElement>;
}

interface SettingsAdjustment<ValueT> {
  invalidReason: SettingInvalidReason;
  overridingParent: Partial<CoreNode>;
  value: ValueT;
}

const RichTextEditorField = dynamic(() => import('../RichTextEditorField/RichTextEditorField'), {
  ssr: false
});

/**
 * Renders custom fields
 * @author Manish Shrestha
 */
const CustomField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  allFields,
  formMethods,
  submitOnChange,
  onFormSubmit,
  i18n,
  formId
}: FormFieldProps<NameT, DataT, CustomFieldSpec<NameT, DataT>>) => {
  const cx = useClassNameMapper(localStyles);
  const { control } = formMethods;
  const {
    name,
    entityId,
    fields,
    settingLabelFunction,
    settingModalFunction,
    isVisible,
    formBodyReference
  } = fieldSpec;
  const { watchFields } = isVisible || {};
  const customDefaultI18n = useTranslation(SharedComponent.CUSTOM_FIELD);
  const { loading: textLoading, hasMessage, formatMessage } = i18n;
  const { loading: customTextLoading, formatMessage: formatDefaultMessage } = customDefaultI18n;
  const watchedValuesObject: Partial<DataT> = useFormWatch(watchFields, control);
  if (textLoading || customTextLoading) {
    return <Loading />;
  }
  if (
    !isFieldVisible<DataT>(
      fieldSpec as unknown as FormFieldSpecDefinition<DataT>,
      watchedValuesObject,
      allFields
    )
  ) {
    return null;
  }

  function getLabelForCustomField(customFieldName: string, required: boolean): string {
    const customFieldLabelKey = `${formId}.custom.${customFieldName}${
      required ? '.required' : ''
    }.label`;
    return hasMessage(customFieldLabelKey)
      ? formatMessage(customFieldLabelKey)
      : formatDefaultMessage(`CustomField.default${required ? '.required' : ''}.label`, {
          name: customFieldName
        });
  }

  function getLabelForSubmitAction(customFieldName: string): string {
    const customFieldLabelKey = `${formId}.custom.${customFieldName}.form.modal.submit`;
    return hasMessage(customFieldLabelKey)
      ? formatMessage(customFieldLabelKey)
      : formatDefaultMessage('CustomField.default.form.modal.submit');
  }

  function getLabelForCancelAction(customFieldName: string): string {
    const customFieldLabelKey = `${formId}.custom.${customFieldName}.form.modal.cancel`;
    return hasMessage(customFieldLabelKey)
      ? formatMessage(customFieldLabelKey)
      : formatDefaultMessage('CustomField.default.form.modal.cancel');
  }

  function getOptionLabelForRadioCustomField(customFieldName: string, option: string): string {
    const customFieldLabelKey = `${formId}.custom.${customFieldName}.${option}.label`;
    return hasMessage(customFieldLabelKey)
      ? formatMessage(customFieldLabelKey)
      : formatDefaultMessage('CustomField.default.radio.option.label', {
          option
        });
  }

  function getOptionLabelForSelectCustomField(customFieldName: string, option: string): string {
    const customFieldLabelKey = `${formId}.custom.${customFieldName}.${option}.select.option.label`;
    return hasMessage(customFieldLabelKey)
      ? formatMessage(customFieldLabelKey)
      : formatDefaultMessage('CustomField.default.select.option.label', {
          option
        });
  }

  function getLabelForNonRemovableField(customFieldName: string): string {
    const customFieldLabelKey = `${formId}.custom.${customFieldName}.removable.label`;
    return hasMessage(customFieldLabelKey)
      ? formatMessage(customFieldLabelKey)
      : formatDefaultMessage('CustomField.default.removable.label', {
          name: customFieldName
        });
  }

  /**
   * Renders a value using the TextChip component
   * @param props
   */
  function renderCustomMultiValue(props): React.ReactElement {
    const {
      data,
      selectProps: { onChange, value },
      isFocused
    } = props;

    return (
      <TextChip
        key={`custom-value-chip-${data.label}`}
        item={{ ...data, valid: true }}
        hasFocus={isFocused}
        onRemove={option => {
          const updatedValues = value.filter(v => v.value !== option.value);
          onChange(updatedValues, {
            action: 'remove-value',
            removedValue: option,
            name: 'custom-values'
          });
        }}
      />
    );
  }

  function adjustSpecForSettings<
    N extends FieldPath<FormDataT>,
    FormDataT extends FieldValues,
    VariantT extends FormFieldVariant,
    SpecT extends FormFieldType<N, FormDataT, VariantT>
  >(
    customName: string,
    required: boolean,
    spec: SpecT,
    adjustment: SettingsAdjustment<FieldPathValue<FormDataT, N>>
  ): SpecT {
    const {
      invalidReason,
      overridingParent: {
        id: overridingParentId,
        title: overridingParentTitle,
        nodeType: overridingParentNodeType
      }
    } = adjustment;
    const labelProps: CustomFieldSettingLabelProps = {
      labelText: getLabelForCustomField(customName, required),
      overridingParentNode:
        entityId !== overridingParentId
          ? {
              title: overridingParentTitle,
              nodeType: overridingParentNodeType
            }
          : undefined
    };
    return {
      ...spec,
      isVisible: {
        watchFields: null,
        callback: () => {
          return invalidReason === null;
        }
      },
      label: settingLabelFunction && settingLabelFunction(labelProps)
    } as SpecT;
  }

  function getCheckField(
    customName: string,
    fieldName: string,
    checkDefaultValue: boolean,
    required: boolean,
    fieldKey: string,
    setting?: InheritableBooleanSetting
  ): React.ReactElement {
    const checkFieldSpec: CheckFieldSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      defaultValue: checkDefaultValue as PathValue<DataT, FieldPath<DataT>>,
      attributes: {
        autoComplete: fieldName
      },
      validations: {
        required: required
      },
      fieldVariant: FormFieldVariant.CHECK,
      label: getLabelForCustomField(customName, required)
    } as CheckFieldSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: CheckFieldSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          checkFieldSpec,
          setting as unknown as SettingsAdjustment<FieldPathValue<DataT, FieldPath<DataT>>>
        )
      : checkFieldSpec;
    const finalFieldSpec: CheckFieldSpec<FieldPath<DataT>, DataT> = setting
      ? {
          ...adjustedFieldSpec,
          inputType: FormCheckInputType.SWITCH,
          variant: CheckFieldVariant.STRETCHED
        }
      : adjustedFieldSpec;
    return (
      <CheckField
        key={fieldKey}
        fieldSpec={finalFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function inputModalField(
    customName: string,
    fieldName: string,
    inputFieldSpec: InputFieldSpec<FieldPath<DataT>, DataT>,
    setting:
      | InheritableDateTimeSetting
      | InheritableFloatSetting
      | InheritableFloatSettingWithPossibleValues
      | InheritableIntSetting
      | InheritableIntSettingWithPossibleValues
      | InheritableLongSetting
      | InheritableStringSetting
      | InheritableStringSettingWithPossibleValues,
    fieldKey: string
  ) {
    const modalFormId = `Custom${capitalizeFirst(customName)}ModelForm`;
    const modalFieldLabelKey = `${modalFormId}.modalfield.label`;
    const modalFormSchema: FormSchema = {
      id: modalFormId,
      fields: [
        {
          id: customName
        }
      ],
      layout: {
        rows: [
          {
            id: customName
          }
        ]
      }
    };
    const singleFieldSpec: InputFieldSpec<FieldPath<DataT>, DataT> = {
      ...inputFieldSpec,
      name: customName as FieldPath<DataT>,
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, false)
        }
      }
    };
    const value = inputFieldSpec.defaultValue ?? '';
    const formSpec: FormSpec<DataT> = new FormBuilder<DataT>(modalFormId, i18n, {
      schema: modalFormSchema,
      cx
    })
      .addField(singleFieldSpec)
      .addSubmitAction({
        size: 'lg',
        title: getLabelForSubmitAction(customName)
      } as FormButton<DataT>)
      .addCancelAction({
        size: 'lg',
        title: getLabelForCancelAction(customName)
      } as FormButton<DataT>)
      .build();

    const {
      invalidReason,
      overridingParent: {
        id: overridingParentId,
        title: overridingParentTitle,
        nodeType: overridingParentNodeType
      }
    } = setting;
    const modalFieldSpec = {
      name: modalFormId as FieldPath<DataT>,
      fieldVariant: FormFieldVariant.PLACEHOLDER_FORM_FIELD,
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, false)
        }
      },
      defaultValue: value,
      isVisible: {
        watchFields: null,
        callback: () => {
          return invalidReason === null;
        }
      } as IsVisibleFieldWatcher<DataT, boolean>,
      Content: () => {
        return settingModalFunction({
          modalFormSpec: formSpec,
          settingValueProps: [
            {
              settingTitle: getLabelForCustomField(customName, false),
              value: value,
              overridingParentNode:
                entityId !== overridingParentId
                  ? {
                      title: overridingParentTitle,
                      nodeType: overridingParentNodeType
                    }
                  : undefined
            }
          ],
          formatDefaultMessage: formatDefaultMessage,
          modalFieldLabelKey: modalFieldLabelKey,
          customName: customName,
          modalSize: 'sm'
        }) as React.ComponentType<React.PropsWithChildren<{ formMethods: UseFormReturn<DataT> }>>;
      }
    } as unknown as PlaceholderFormFieldSpec<NameT, DataT>;
    return (
      <PlaceholderFormField<NameT, DataT>
        key={fieldKey}
        formId={modalFormId}
        fieldSpec={modalFieldSpec}
        className={cx('lia-g-setting-form-field-divider')}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function inputField(
    customName: string,
    customFormFieldType: CustomFieldValueType,
    fieldName: string,
    defaultValue: string | DateTimeValue | boolean | string[],
    required: boolean,
    removable: boolean,
    fieldKey: string,
    inputType: FormInputFieldInputType,
    setting?:
      | InheritableDateTimeSetting
      | InheritableFloatSetting
      | InheritableFloatSettingWithPossibleValues
      | InheritableIntSetting
      | InheritableIntSettingWithPossibleValues
      | InheritableLongSetting
      | InheritableStringSetting
      | InheritableStringSettingWithPossibleValues,
    min?: number | string,
    max?: number | string,
    values?: FloatPossibleValue[] | IntPossibleValue[] | StringPossibleValue[],
    minLength?: number,
    maxLength?: number
  ): React.ReactElement {
    const inputFieldSpec: InputFieldSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      defaultValue: defaultValue as PathValue<DataT, FieldPath<DataT>>,
      fieldVariant: FormFieldVariant.INPUT,
      inputType,
      validations: {
        min: min,
        max: max,
        minLength: minLength,
        maxLength: maxLength,
        required: required,
        removable: removable,
        validate: values
          ? {
              possibleValues: value => {
                return values
                  .map(({ value: possibleValue }) => possibleValue)
                  .some(possibleValue => value == possibleValue);
              }
            }
          : !removable
          ? formData => {
              if (!formData && defaultValue != '') {
                return getLabelForNonRemovableField(fieldName);
              }
            }
          : undefined
      },
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, required)
        }
      }
    } as InputFieldSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: InputFieldSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          inputFieldSpec,
          setting as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : inputFieldSpec;
    if (setting) {
      return inputModalField(customName, fieldName, inputFieldSpec, setting, fieldKey);
    }
    return (
      <InputField
        key={fieldKey}
        fieldSpec={adjustedFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function getRadioField(
    customName: string,
    fieldName: string,
    defaultValue: string | DateTimeValue | boolean | string[],
    required: boolean,
    fieldKey: string,
    setting?:
      | InheritableFloatSetting
      | InheritableFloatSettingWithPossibleValues
      | InheritableIntSetting
      | InheritableIntSettingWithPossibleValues
      | InheritableStringSetting
      | InheritableStringSettingWithPossibleValues,
    min?: number,
    max?: number,
    values?: FloatPossibleValue[] | IntPossibleValue[] | StringPossibleValue[],
    minLength?: number,
    maxLength?: number
  ): React.ReactElement {
    const possibleValues: PossibleValueType<
      FormFieldVariant.RADIO,
      FieldPath<DataT>,
      DataT
    > = values.map(({ key, value, description, label }) => {
      return {
        key,
        value: value,
        description,
        label: label ?? getOptionLabelForRadioCustomField(customName, key)
      } as PossibleValue<DataT[NameT], DataT>;
    });
    const radioFieldSpec: RadioFieldSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      defaultValue: defaultValue as PathValue<DataT, FieldPath<DataT>>,
      values: possibleValues,
      fieldVariant: FormFieldVariant.RADIO,
      validations: {
        min: min,
        max: max,
        minLength: minLength,
        maxLength: maxLength,
        required: required,
        validate: values
          ? {
              possibleValues: value => {
                return values
                  .map(({ value: possibleValue }) => possibleValue)
                  .some(possibleValue => value == possibleValue);
              }
            }
          : undefined
      },
      fieldsetSpec: {
        legend: {
          label: getLabelForCustomField(customName, required)
        }
      }
    } as RadioFieldSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: RadioFieldSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          radioFieldSpec,
          setting as unknown as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : radioFieldSpec;
    return (
      <RadioField
        key={fieldKey}
        fieldSpec={adjustedFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function getRangeField(
    customName: string,
    fieldName: string,
    defaultValue: string | DateTimeValue | boolean | string[],
    required: boolean,
    fieldKey: string,
    min: number,
    max: number,
    setting?:
      | InheritableFloatSetting
      | InheritableFloatSettingWithPossibleValues
      | InheritableIntSetting
      | InheritableIntSettingWithPossibleValues
      | InheritableLongSetting
  ): React.ReactElement {
    const rangeFieldSpec: RangeFieldSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      defaultValue: defaultValue as PathValue<DataT, FieldPath<DataT>>,
      fieldVariant: FormFieldVariant.RANGE,
      min: min,
      max: max,
      validations: {
        min: min,
        max: max,
        required: required
      },
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, required)
        }
      }
    } as RangeFieldSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: RangeFieldSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          rangeFieldSpec,
          setting as unknown as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : rangeFieldSpec;
    return (
      <RangeField
        key={fieldKey}
        fieldSpec={adjustedFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function getSelectField(
    customName: string,
    fieldName: string,
    defaultValue: string | DateTimeValue | boolean | string[],
    required: boolean,
    fieldKey: string,
    setting?:
      | InheritableFloatSetting
      | InheritableFloatSettingWithPossibleValues
      | InheritableIntSetting
      | InheritableIntSettingWithPossibleValues
      | InheritableStringSetting
      | InheritableStringSettingWithPossibleValues,
    min?: number,
    max?: number,
    values?: FloatPossibleValue[] | IntPossibleValue[] | StringPossibleValue[],
    minLength?: number,
    maxLength?: number
  ): React.ReactElement {
    const selectFieldSpec: SelectFieldSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      defaultValue: defaultValue as PathValue<DataT, FieldPath<DataT>>,
      fieldVariant: FormFieldVariant.SELECT,
      values: values.map(({ key, description, value, label }) => {
        return {
          key,
          value: value,
          description,
          label: label ?? getOptionLabelForSelectCustomField(customName, key)
        } as PossibleValue<DataT[NameT], DataT>;
      }),
      validations: {
        min,
        max,
        minLength,
        maxLength,
        required
      },
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, required)
        }
      }
    } as SelectFieldSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: SelectFieldSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          selectFieldSpec,
          setting as unknown as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : selectFieldSpec;
    const liaFieldClassName = setting ? 'lia-field' : undefined;
    return (
      <SelectField
        className={liaFieldClassName}
        key={fieldKey}
        fieldSpec={adjustedFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function getMultiSelectField(
    customName: string,
    fieldName: string,
    defaultValue: string[],
    required: boolean,
    fieldKey: string,
    setting?:
      | InheritableFloatListSettingWithPossibleValues
      | InheritableIntListSettingWithPossibleValues
      | InheritableStringListSettingWithPossibleValues,
    values?: FloatPossibleValue[] | IntPossibleValue[] | StringPossibleValue[]
  ): React.ReactElement {
    const selectFieldSpec: SearchableMultiSelectFieldSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      defaultValue: defaultValue.map(defaultValue_ => {
        const { key, value, description, label } = values.find(
          option => option.value == defaultValue_
        );
        return {
          key,
          value,
          description,
          label: label ?? getOptionLabelForSelectCustomField(customName, key)
        };
      }) as PathValue<DataT, FieldPath<DataT>>,
      validations: {
        required
      },
      fieldVariant: FormFieldVariant.SEARCHABLE_MULTI_SELECT,
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, required)
        }
      },
      loadOptions: values.map(({ key, description, value, label }) => {
        return {
          key,
          value,
          description,
          label: label ?? getOptionLabelForSelectCustomField(customName, key)
        } as PossibleValue<DataT[NameT], DataT>;
      }),
      customMultiValue: renderCustomMultiValue
    } as SearchableMultiSelectFieldSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: SearchableMultiSelectFieldSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          selectFieldSpec,
          setting as unknown as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : selectFieldSpec;
    const liaFieldClassName = setting ? 'lia-field' : undefined;
    return (
      <SearchableMultiSelectField
        className={liaFieldClassName}
        key={fieldKey}
        fieldSpec={adjustedFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function getDateTimeField(
    backDate: RelativeDate,
    forwardDate: RelativeDate,
    customName: string,
    fieldName: string,
    dateTimeDefaultValue: DateTimeValue,
    required: boolean,
    fieldKey: string,
    setting?: InheritableDateTimeSetting
  ): React.ReactElement {
    const currentTime = new Date();
    const minDate = getRelativeDate(backDate, false, currentTime);
    const maxDate = getRelativeDate(forwardDate, true, currentTime);
    const datetimeSpec: DatetimeSpec<FieldPath<DataT>, DataT> = {
      fieldVariant: FormFieldVariant.DATETIME,
      name: fieldName as FieldPath<DataT>,
      defaultValue: dateTimeDefaultValue as PathValue<DataT, FieldPath<DataT>>,
      attributes: {
        autoComplete: fieldName
      },
      minDate,
      maxDate,
      validations: {
        required: required,
        min: minDate.getTime(),
        max: maxDate.getTime()
      },
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, required)
        }
      }
    } as DatetimeSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: DatetimeSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          datetimeSpec,
          setting as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : datetimeSpec;
    return (
      <DateTimeEditorField
        key={fieldKey}
        fieldSpec={adjustedFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function getPasswordField(
    customName: string,
    fieldName: string,
    defaultValue: string,
    minLength: number,
    maxLength: number,
    required: boolean,
    fieldKey: string,
    setting?: InheritableStringSetting | InheritableStringSettingWithPossibleValues
  ): React.ReactElement {
    const passwordFieldSpec: PasswordFieldSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      defaultValue: defaultValue as PathValue<DataT, FieldPath<DataT>>,
      fieldVariant: FormFieldVariant.PASSWORD,
      inputType: FormInputFieldInputType.PASSWORD,
      validations: {
        minLength: minLength,
        maxLength: maxLength,
        required: required
      },
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, required)
        }
      }
    } as PasswordFieldSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: PasswordFieldSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          passwordFieldSpec,
          setting as unknown as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : passwordFieldSpec;
    return (
      <PasswordField
        key={fieldKey}
        fieldSpec={adjustedFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function textAreaModalField(
    customName: string,
    fieldName: string,
    textAreaSpec: TextAreaSpec<FieldPath<DataT>, DataT>,
    setting: InheritableStringSetting,
    fieldKey: string
  ) {
    const modalFormId = `Custom${capitalizeFirst(customName)}ModelForm`;
    const modalFieldLabelKey = `${modalFormId}.modalfield.label`;
    const modalFormSchema: FormSchema = {
      id: modalFormId,
      fields: [
        {
          id: customName
        }
      ],
      layout: {
        rows: [
          {
            id: customName
          }
        ]
      }
    };
    const singleFieldSpec: TextAreaSpec<FieldPath<DataT>, DataT> = {
      ...textAreaSpec,
      name: customName as FieldPath<DataT>,
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, false)
        }
      }
    };
    const value = textAreaSpec.defaultValue ?? '';
    const formSpec: FormSpec<DataT> = new FormBuilder<DataT>(modalFormId, i18n, {
      schema: modalFormSchema,
      cx
    })
      .addField(singleFieldSpec)
      .addSubmitAction({
        size: 'lg',
        title: getLabelForSubmitAction(customName)
      } as FormButton<DataT>)
      .addCancelAction({
        size: 'lg',
        title: getLabelForCancelAction(customName)
      } as FormButton<DataT>)
      .build();

    const {
      invalidReason,
      overridingParent: {
        id: overridingParentId,
        title: overridingParentTitle,
        nodeType: overridingParentNodeType
      }
    } = setting;
    const modalFieldSpec = {
      name: modalFormId as FieldPath<DataT>,
      fieldVariant: FormFieldVariant.PLACEHOLDER_FORM_FIELD,
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, false)
        }
      },
      defaultValue: value,
      isVisible: {
        watchFields: null,
        callback: () => {
          return invalidReason === null;
        }
      } as IsVisibleFieldWatcher<DataT, boolean>,
      Content: () => {
        return settingModalFunction({
          modalFormSpec: formSpec,
          settingValueProps: [
            {
              settingTitle: getLabelForCustomField(customName, false),
              value: value,
              overridingParentNode:
                entityId !== overridingParentId
                  ? {
                      title: overridingParentTitle,
                      nodeType: overridingParentNodeType
                    }
                  : undefined
            }
          ],
          formatDefaultMessage: formatDefaultMessage,
          modalFieldLabelKey: modalFieldLabelKey,
          customName: customName,
          modalSize: 'lg'
        }) as React.ComponentType<React.PropsWithChildren<{ formMethods: UseFormReturn<DataT> }>>;
      }
    } as unknown as PlaceholderFormFieldSpec<NameT, DataT>;
    return (
      <PlaceholderFormField<NameT, DataT>
        key={fieldKey}
        formId={modalFormId}
        fieldSpec={modalFieldSpec}
        className={cx('lia-g-setting-form-field-divider')}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function getTextAreaField(
    customName: string,
    fieldName: string,
    defaultValue: string,
    minLength: number,
    maxLength: number,
    fieldKey: string,
    required: boolean,
    setting?: InheritableStringSetting
  ): React.ReactElement {
    const textAreaSpec: TextAreaSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      defaultValue: defaultValue as PathValue<DataT, FieldPath<DataT>>,
      fieldVariant: FormFieldVariant.TEXT_AREA,
      validations: {
        minLength,
        maxLength,
        required
      },
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, required)
        }
      }
    } as TextAreaSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: TextAreaSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          textAreaSpec,
          setting as unknown as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : textAreaSpec;
    if (setting) {
      return textAreaModalField(customName, fieldName, textAreaSpec, setting, fieldKey);
    }
    return (
      <TextAreaField
        fieldSpec={adjustedFieldSpec}
        formId={formId}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function rteModalField(
    customName: string,
    fieldName: string,
    richTextEditorSpec: RichTextEditorSpec<FieldPath<DataT>, DataT>,
    setting: InheritableStringSetting,
    fieldKey: string
  ) {
    const modalFormId = `Custom${capitalizeFirst(customName)}ModelForm`;
    const modalFieldLabelKey = `${modalFormId}.modalfield.label`;
    const modalFormSchema: FormSchema = {
      id: modalFormId,
      fields: [
        {
          id: customName
        }
      ],
      layout: {
        rows: [
          {
            id: 'form-body',
            type: 'fieldset',
            items: [
              {
                id: customName
              }
            ]
          }
        ]
      }
    };
    const singleFieldSpec: RichTextEditorSpec<FieldPath<DataT>, DataT> = {
      ...richTextEditorSpec,
      name: customName as FieldPath<DataT>,
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, false)
        }
      }
    };
    const value = richTextEditorSpec.defaultValue ?? '';
    const formSpec: FormSpec<DataT> = new FormBuilder<DataT>(modalFormId, i18n, {
      schema: modalFormSchema as FormSchema,
      cx
    })
      .addField(singleFieldSpec)
      .addSubmitAction({
        size: 'lg',
        title: getLabelForSubmitAction(customName)
      } as FormButton<DataT>)
      .addCancelAction({
        size: 'lg',
        title: getLabelForCancelAction(customName)
      } as FormButton<DataT>)
      .build();

    const {
      invalidReason,
      overridingParent: {
        id: overridingParentId,
        title: overridingParentTitle,
        nodeType: overridingParentNodeType
      }
    } = setting;
    const modalFieldSpec = {
      name: modalFormId as FieldPath<DataT>,
      fieldVariant: FormFieldVariant.PLACEHOLDER_FORM_FIELD,
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, false)
        }
      },
      defaultValue: value,
      isVisible: {
        watchFields: null,
        callback: () => {
          return invalidReason === null;
        }
      } as IsVisibleFieldWatcher<DataT, boolean>,
      Content: () => {
        return settingModalFunction({
          modalFormSpec: formSpec,
          settingValueProps: [
            {
              settingTitle: getLabelForCustomField(customName, false),
              value: value,
              overridingParentNode:
                entityId !== overridingParentId
                  ? {
                      title: overridingParentTitle,
                      nodeType: overridingParentNodeType
                    }
                  : undefined
            }
          ],
          formatDefaultMessage: formatDefaultMessage,
          modalFieldLabelKey: modalFieldLabelKey,
          customName: customName,
          modalSize: 'lg'
        }) as React.ComponentType<React.PropsWithChildren<{ formMethods: UseFormReturn<DataT> }>>;
      }
    } as unknown as PlaceholderFormFieldSpec<NameT, DataT>;
    return (
      <PlaceholderFormField<NameT, DataT>
        key={fieldKey}
        formId={modalFormId}
        fieldSpec={modalFieldSpec}
        className={cx('lia-g-setting-form-field-divider')}
        i18n={i18n}
        allFields={allFields}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  function getRteField(
    customName: string,
    fieldName: string,
    defaultValue: string,
    fieldKey: string,
    required: boolean,
    setting?: InheritableStringSetting
  ): React.ReactElement {
    const richTextEditorSpec: RichTextEditorSpec<FieldPath<DataT>, DataT> = {
      name: fieldName as FieldPath<DataT>,
      fieldVariant: FormFieldVariant.RTE,
      validations: {
        required: required
      },
      defaultValue: (defaultValue as PathValue<DataT, FieldPath<DataT>>) || '',
      // LIA-96495 see getToolbarContainerId function in RichTextEditorField.tsx
      contentWrapperClassName: cx('lia-rte-wrap lia-g-message-body'),
      externalToolbarClassname: cx('lia-rte-toolbar'),
      transparentDragover: true,
      compactDragover: true,
      editorSettings: editorSettingsForCustomField(),
      focus: true,
      isMentionsEnabled: false,
      overlayContainerRef: formBodyReference,
      formGroupSpec: {
        label: {
          labelTextOverride: getLabelForCustomField(customName, required)
        }
      }
    } as RichTextEditorSpec<FieldPath<DataT>, DataT>;
    const adjustedFieldSpec: RichTextEditorSpec<FieldPath<DataT>, DataT> = setting
      ? adjustSpecForSettings(
          customName,
          required,
          richTextEditorSpec,
          setting as unknown as SettingsAdjustment<PathValue<DataT, FieldPath<DataT>>>
        )
      : richTextEditorSpec;
    if (setting) {
      return rteModalField(customName, fieldName, richTextEditorSpec, setting, fieldKey);
    }
    return (
      <RichTextEditorField
        key={fieldKey}
        fieldSpec={adjustedFieldSpec as unknown as RichTextEditorSpec<string, FieldValues>}
        formId={formId}
        i18n={i18n}
        allFields={allFields as unknown as Array<FormFieldSpecDefinition<FieldValues>>}
        formMethods={formMethods as unknown as UseFormReturn}
        submitOnChange={submitOnChange as unknown as DebounceProps<FieldValues>}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  return fields.map(field => {
    const { valueType: formFieldType, name: customName } = field;
    const fieldName = `${name}.${customName}`;
    const fieldKey = `${formId}-${fieldName}`;
    const defaultValue = getDefaultValueForCustomField(field);
    const removable = field.removable ?? true;
    const customFormFieldType: CustomFieldValueType = formFieldType as CustomFieldValueType;
    switch (customFormFieldType) {
      case CustomFieldValueType.Boolean: {
        const { booleanSetting, required } = field as BooleanEntityCustomFormField;
        return getCheckField(
          customName,
          fieldName,
          defaultValue as boolean,
          required,
          fieldKey,
          booleanSetting
        );
      }
      case CustomFieldValueType.Datetime: {
        const { dateTimeField, dateTimeSetting, required } = field as DateTimeEntityCustomFormField;
        const { dateTimeType } = dateTimeField;
        const formFieldControlType: DateTimeCustomFormFieldType =
          dateTimeType as DateTimeCustomFormFieldType;
        switch (formFieldControlType) {
          case DateTimeCustomFormFieldType.Datetime: {
            const { backDate, forwardDate } = dateTimeField as DateTimeField;
            return getDateTimeField(
              backDate,
              forwardDate,
              customName,
              fieldName,
              defaultValue as DateTimeValue,
              required,
              fieldKey,
              dateTimeSetting
            );
          }
          case DateTimeCustomFormFieldType.Input: {
            const { dateTimeInputType, dateTimeInputMin, dateTimeInputMax } =
              dateTimeField as DateTimeInputField;
            const inputType: FormInputFieldInputType = getFormFieldInputType(dateTimeInputType);
            return inputField(
              customName,
              customFormFieldType,
              fieldName,
              defaultValue,
              required,
              removable,
              fieldKey,
              inputType,
              dateTimeSetting,
              dateTimeInputMin,
              dateTimeInputMax
            );
          }
        }
        break;
      }
      case CustomFieldValueType.Float: {
        const { floatField, floatSetting, required } = field as FloatEntityCustomFormField;
        const { floatType } = floatField;
        const formFieldControlType: FloatCustomFormFieldType =
          floatType as FloatCustomFormFieldType;
        switch (formFieldControlType) {
          case FloatCustomFormFieldType.Input: {
            const { floatInputType, floatInputMin, floatInputMax, floatInputValues } =
              floatField as FloatInputField;
            const inputType: FormInputFieldInputType = getFormFieldInputType(floatInputType);
            return inputField(
              customName,
              customFormFieldType,
              fieldName,
              defaultValue,
              required,
              removable,
              fieldKey,
              inputType,
              floatSetting,
              floatInputMin,
              floatInputMax,
              floatInputValues
            );
          }
          case FloatCustomFormFieldType.Radio: {
            const { floatRadioMin, floatRadioMax, floatRadioValues } =
              floatField as FloatRadioField;
            return getRadioField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              floatSetting,
              floatRadioMin,
              floatRadioMax,
              floatRadioValues
            );
          }
          case FloatCustomFormFieldType.Range: {
            const { floatRangeMin, floatRangeMax } = floatField as FloatRangeField;
            return getRangeField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              floatRangeMin,
              floatRangeMax,
              floatSetting
            );
          }
          case FloatCustomFormFieldType.Select: {
            const { floatSelectMin, floatSelectMax, floatSelectValues } =
              floatField as FloatSelectField;
            return getSelectField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              floatSetting,
              floatSelectMin,
              floatSelectMax,
              floatSelectValues
            );
          }
        }
        break;
      }
      case CustomFieldValueType.Int: {
        const { intField, intSetting, required } = field as IntEntityCustomFormField;
        const { intType } = intField;
        const formFieldControlType: IntCustomFormFieldType = intType as IntCustomFormFieldType;
        switch (formFieldControlType) {
          case IntCustomFormFieldType.Input: {
            const { intInputType, intInputMin, intInputMax, intInputValues } =
              intField as IntInputField;
            const inputType: FormInputFieldInputType = getFormFieldInputType(intInputType);
            return inputField(
              customName,
              customFormFieldType,
              fieldName,
              defaultValue,
              required,
              removable,
              fieldKey,
              inputType,
              intSetting,
              intInputMin,
              intInputMax,
              intInputValues
            );
          }
          case IntCustomFormFieldType.Radio: {
            const { intRadioMin, intRadioMax, intRadioValues } = intField as IntRadioField;
            return getRadioField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              intSetting,
              intRadioMin,
              intRadioMax,
              intRadioValues
            );
          }
          case IntCustomFormFieldType.Range: {
            const { intRangeMin, intRangeMax } = intField as IntRangeField;
            return getRangeField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              intRangeMin,
              intRangeMax,
              intSetting
            );
          }
          case IntCustomFormFieldType.Select: {
            const { intSelectMin, intSelectMax, intSelectValues } = intField as IntSelectField;
            return getSelectField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              intSetting,
              intSelectMin,
              intSelectMax,
              intSelectValues
            );
          }
        }
        break;
      }
      case CustomFieldValueType.Long: {
        const { longField, longSetting, required } = field as LongEntityCustomFormField;
        const { longType } = longField;
        const formFieldControlType: LongCustomFormFieldType = longType as LongCustomFormFieldType;
        switch (formFieldControlType) {
          case LongCustomFormFieldType.Input: {
            const { longInputType, longInputMin, longInputMax } = longField as LongInputField;
            const inputType: FormInputFieldInputType = getFormFieldInputType(longInputType);
            return inputField(
              customName,
              customFormFieldType,
              fieldName,
              defaultValue,
              required,
              removable,
              fieldKey,
              inputType,
              longSetting,
              longInputMin,
              longInputMax
            );
          }
          case LongCustomFormFieldType.Range: {
            const { longRangeMin, longRangeMax } = longField as LongRangeField;
            return getRangeField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              longRangeMin,
              longRangeMax,
              longSetting
            );
          }
        }
        break;
      }
      case CustomFieldValueType.String: {
        const { stringField, stringSetting, required } = field as StringEntityCustomFormField;
        const { stringType } = stringField;
        const formFieldControlType: StringCustomFormFieldType =
          stringType as StringCustomFormFieldType;
        switch (formFieldControlType) {
          case StringCustomFormFieldType.Input: {
            const {
              stringInputType,
              stringInputMinLength,
              stringInputMaxLength,
              stringInputValues
            } = stringField as StringInputField;
            const inputType: FormInputFieldInputType = getFormFieldInputType(stringInputType);
            return inputField(
              customName,
              customFormFieldType,
              fieldName,
              defaultValue,
              required,
              removable,
              fieldKey,
              inputType,
              stringSetting,
              undefined,
              undefined,
              stringInputValues,
              stringInputMinLength,
              stringInputMaxLength
            );
          }
          case StringCustomFormFieldType.Radio: {
            const { stringRadioMinLength, stringRadioMaxLength, stringRadioValues } =
              stringField as StringRadioField;
            return getRadioField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              stringSetting,
              undefined,
              undefined,
              stringRadioValues,
              stringRadioMinLength,
              stringRadioMaxLength
            );
          }
          case StringCustomFormFieldType.Select: {
            const { stringSelectMinLength, stringSelectMaxLength, stringSelectValues } =
              stringField as StringSelectField;
            return getSelectField(
              customName,
              fieldName,
              defaultValue,
              required,
              fieldKey,
              stringSetting,
              undefined,
              undefined,
              stringSelectValues,
              stringSelectMinLength,
              stringSelectMaxLength
            );
          }
          case StringCustomFormFieldType.Password: {
            const { passwordMinLength, passwordMaxLength } = stringField as PasswordFieldInput;
            return getPasswordField(
              customName,
              fieldName,
              defaultValue as string,
              passwordMinLength,
              passwordMaxLength,
              required,
              fieldKey,
              stringSetting
            );
          }
          case StringCustomFormFieldType.Textarea: {
            const { textareaMinLength, textareaMaxLength } = stringField as TextareaFieldInput;
            return getTextAreaField(
              customName as string,
              fieldName as string,
              defaultValue as string,
              textareaMinLength as number,
              textareaMaxLength as number,
              fieldKey as string,
              required,
              stringSetting as InheritableStringSetting
            );
          }
          case StringCustomFormFieldType.Rte: {
            return getRteField(
              customName,
              fieldName,
              defaultValue as string,
              fieldKey,
              required,
              stringSetting as InheritableStringSetting
            );
          }
          default: {
            break;
          }
        }
        break;
      }
      case CustomFieldValueType.ListString: {
        const { stringListField, stringListSetting, required } =
          field as ListStringEntityCustomFormField;
        const { stringListType } = stringListField;
        const formFieldControlType: StringListCustomFormFieldType =
          stringListType as StringListCustomFormFieldType;
        switch (formFieldControlType) {
          case StringListCustomFormFieldType.Select: {
            const { stringListSelectValues } = stringListField as StringListSelectField;
            return getMultiSelectField(
              customName,
              fieldName,
              defaultValue as string[],
              required,
              fieldKey,
              stringListSetting,
              stringListSelectValues
            );
          }
        }
        break;
      }
      case CustomFieldValueType.ListInt: {
        const { intListField, intListSetting, required } = field as ListIntEntityCustomFormField;
        const { intListType } = intListField;
        const formFieldControlType: IntListCustomFormFieldType =
          intListType as IntListCustomFormFieldType;
        switch (formFieldControlType) {
          case IntListCustomFormFieldType.Select: {
            const { intListSelectValues } = intListField as IntListSelectField;
            return getMultiSelectField(
              customName,
              fieldName,
              defaultValue as string[],
              required,
              fieldKey,
              intListSetting,
              intListSelectValues
            );
          }
        }
        break;
      }
      case CustomFieldValueType.ListFloat: {
        const { floatListField, floatListSetting, required } =
          field as ListFloatEntityCustomFormField;
        const { floatListType } = floatListField;
        const formFieldControlType: FloatListCustomFormFieldType =
          floatListType as FloatListCustomFormFieldType;
        switch (formFieldControlType) {
          case FloatListCustomFormFieldType.Select: {
            const { floatListSelectValues } = floatListField as FloatListSelectField;
            return getMultiSelectField(
              customName,
              fieldName,
              defaultValue as string[],
              required,
              fieldKey,
              floatListSetting,
              floatListSelectValues
            );
          }
        }
        break;
      }
    }
  });
};

export default CustomField;
